import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

export type CommentRow = {
  id: number;
  entity_type: "person" | "vendor";
  entity_id: number;
  body: string;
  author: string;
  created_at: string;
};

export function useComments(
  entityType: "person" | "vendor",
  entityId: number | undefined,
) {
  const api = useApi();
  return useQuery({
    queryKey: ["directory_comments", entityType, entityId],
    enabled: entityId != null,
    queryFn: async (): Promise<CommentRow[]> => {
      const res = await api(
        `/api/directory_comments?entity_type=${entityType}&entity_id=${entityId}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as CommentRow[];
    },
    staleTime: 15_000,
  });
}

export function useCreateComment(
  entityType: "person" | "vendor",
  entityId: number | undefined,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; author?: string }): Promise<CommentRow> => {
      const res = await api(`/api/directory_comments`, {
        method: "POST",
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          body: input.body,
          author: input.author,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return (await res.json()) as CommentRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["directory_comments", entityType, entityId] });
    },
  });
}

export function useDeleteComment(
  entityType: "person" | "vendor",
  entityId: number | undefined,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string): Promise<void> => {
      const res = await api(`/api/directory_comments/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["directory_comments", entityType, entityId] });
    },
  });
}
